import React from 'react'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { applyUploadcareModifiers } from '@jarvis.oddle/core/tools/uploadcare'
import { makeRestaurantSchema } from 'src/utils/product'
import { getSectionFromId } from '@jarvis.oddle/core/tools/customisation'
import useAppSelector from 'src/hooks/useAppSelector'
import {
  selectSectionSettingData,
  selectSiteSettingData
} from 'src/redux/customisation'
import { selectDetailMenuSite } from 'src/redux/meta'
import { selectMenuProductList } from 'src/redux/menu/selector'
import { selectStore } from 'src/redux/stores'
import { getCustomSEOByPage } from 'src/utils/customMetaSEO'

const makeFilledImageOptions = (dimensions) => ({
  format: 'png',
  stretch: 'fill',
  preview: dimensions,
  resize: dimensions
})

const launchImageConfigs = [
  {
    media:
      '(device-width: 375px) and (device-height: 812px) and (-webkit-device-pixel-ratio: 3)',
    dimensions: '1125x2436'
  },
  {
    media:
      '(device-width: 375px) and (device-height: 667px) and (-webkit-device-pixel-ratio: 2)',
    dimensions: '750x1334'
  },
  {
    media:
      '(device-width: 414px) and (device-height: 736px) and (-webkit-device-pixel-ratio: 3)',
    dimensions: '1242x2208'
  },
  {
    media:
      '(device-width: 320px) and (device-height: 568px) and (-webkit-device-pixel-ratio: 2)',
    dimensions: '640x1136'
  },
  {
    media:
      '(device-width: 768px) and (device-height: 1024px) and (-webkit-device-pixel-ratio: 2',
    dimensions: '1536x2048'
  },
  {
    media:
      '(device-width: 834px) and (device-height: 1112px) and (-webkit-device-pixel-ratio: 2)',
    dimensions: '1668x2224'
  },
  {
    media:
      '(device-width: 1024px) and (device-height: 1366px) and (-webkit-device-pixel-ratio: 2)',
    dimensions: '2048x2732'
  }
]

const getLaunchImages = ({ image, fill }) =>
  launchImageConfigs.map((c) => ({
    ...c,
    href: applyUploadcareModifiers(image, {
      stretch: 'fill',
      setfill: fill && fill.replace(/#/, ''),
      preview: '300x300',
      resize: c.dimensions,
      format: 'jpeg'
    })
  }))

const defaultLogo = 'https://ucarecdn.com/32a9914d-8da0-4647-a067-28d9f771d78e/'

const replaceMenuName = (string, menuName) =>
  (string || '').replace(/{{menuName}}/g, menuName || '')

interface PageHeadProps {
  currentUrl: string
}

const PageHead = ({ currentUrl }: PageHeadProps): JSX.Element => {
  const router = useRouter()
  const meta = useAppSelector(selectDetailMenuSite)
  const siteSettingData = useAppSelector(selectSiteSettingData)
  const store = useAppSelector(selectStore)
  const { data: menu } = useAppSelector(selectMenuProductList)
  const sectionSettingData = useAppSelector(selectSectionSettingData)
  const { name, cartDesc, cartMetaTag } = meta?.menu || {}
  const { homePageTitle, homePageMetaDescription, siteKeywordsTag, favicon } =
    siteSettingData || {}
  const title = replaceMenuName(homePageTitle, name) || name
  const metaDescription = (
    replaceMenuName(homePageMetaDescription, name) ||
    cartDesc ||
    ''
  ).substr(0, 150)
  const keywordsTag = siteKeywordsTag || (cartMetaTag as string)
  // https://stackoverflow.com/questions/4014823/does-a-favicon-have-to-be-32x32-or-16x16
  const icon =
    favicon ||
    applyUploadcareModifiers(defaultLogo, makeFilledImageOptions('310x310'))
  const isCheckoutPage = router.pathname === '/[locale]/checkout/[storeOrderId]' || router.pathname === '/[locale]/invoice/[storeOrderId]/[hashCode]'

  const { headerBackgroundColor } =
    getSectionFromId({
      sectionId: 'header',
      sectionSettingData
    }).settings || {}

  // https://medium.com/appscope/adding-custom-ios-splash-screens-to-your-progressive-web-app-41a9b18bdca3
  const launchImages = getLaunchImages({
    image: favicon || defaultLogo,
    fill: headerBackgroundColor
  })

  const { customTitle, customDescription, customKeywordsTag } = getCustomSEOByPage({
    pathname: router.pathname,
    menuName: name,
    title,
    metaDescription,
    keywordsTag
  })

  return (
    <Head>
      <title>{customTitle}</title>
      <meta key='og:title' property='og:title' content={customTitle} />
      <meta key='twitter:title' name='twitter:title' content={customTitle} />
      <meta itemProp='name' content={customTitle} />
      <meta key='og:site_name' property='og:site_name' content={customTitle} />
      <meta name='apple-mobile-web-app-title' content={customTitle} />
      <meta name='theme-color' content={headerBackgroundColor} />
      {customKeywordsTag && <meta name='keywords' content={customKeywordsTag} />}
      {customDescription && <meta name='description' content={customDescription} />}
      {customDescription && (
        <meta
          key='og:description'
          property='og:description'
          content={customDescription}
        />
      )}
      {customDescription && (
        <meta
          key='twitter:description'
          name='twitter:description'
          content={customDescription}
        />
      )}
      {customDescription && (
        <meta itemProp='description' content={customDescription} />
      )}
      {icon && <link rel='icon' href={icon} />}
      {icon && <link rel='apple-touch-icon' href={icon} />}
      {icon && <meta name='msapplication-square310x310logo' content={icon} />}
      {favicon && <meta key='og:image' property='og:image' content={favicon} />}
      {favicon && <meta property='image' content={favicon} />}
      {favicon && <meta itemProp='image' content={favicon} />}
      {favicon && (
        <meta key='twitter:image' name='twitter:image' content={favicon} />
      )}
      {currentUrl && (
        <meta key='og:url' property='og:url' content={`https:${currentUrl}`} />
      )}
      {currentUrl && <link rel='canonical' href={`https:${currentUrl}`} />}
      {launchImages.map((image, index) => (
        <link
          key={index}
          rel='apple-touch-startup-image'
          media={image.media}
          href={image.href}
        />
      ))}
      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            makeRestaurantSchema({
              menu,
              meta: meta,
              siteSettingData,
              store,
              currentUrl,
              sections: sectionSettingData.sections
            })
          )
        }}
      />
    </Head>
  )
}

export default PageHead
